import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const { t, language } = useI18n();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error(language === 'es' ? 'Por favor introduce tu email' : 'Please enter your email');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setSent(true);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail ? t(errorData.detail) : (language === 'es' ? 'Error al enviar el email' : 'Error sending email'));
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error de conexión' : 'Connection error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-white to-cream flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="/logo.svg" alt="MealPlanner" className="w-10 h-10" />
            <span className="font-manrope font-bold text-2xl text-charcoal">MealPlanner</span>
          </Link>
        </div>

        <Card className="bg-white rounded-3xl shadow-soft p-8">
          {!sent ? (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h1 className="font-manrope font-bold text-2xl text-charcoal mb-2">
                  {language === 'es' ? '¿Olvidaste tu contraseña?' : 'Forgot your password?'}
                </h1>
                <p className="font-inter text-stone">
                  {language === 'es'
                    ? 'Introduce tu email y te enviaremos un enlace para restablecerla'
                    : 'Enter your email and we\'ll send you a reset link'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-manrope text-charcoal">
                    {language === 'es' ? 'Email' : 'Email'}
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone" strokeWidth={1.5} />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="pl-10 h-12 rounded-xl border-border bg-white/50 font-inter"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl h-12 font-manrope font-semibold"
                  data-testid="forgot-submit-button"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    language === 'es' ? 'Enviar enlace' : 'Send reset link'
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-sage/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-sage" strokeWidth={1.5} />
              </div>
              <h2 className="font-manrope font-bold text-2xl text-charcoal mb-2">
                {language === 'es' ? '¡Revisa tu email!' : 'Check your email!'}
              </h2>
              <p className="font-inter text-stone mb-6">
                {language === 'es'
                  ? `Si existe una cuenta con ${email}, recibirás un enlace para restablecer tu contraseña.`
                  : `If an account exists with ${email}, you'll receive a reset link.`}
              </p>
              <p className="font-inter text-sm text-stone/70">
                {language === 'es'
                  ? '¿No lo ves? Revisa tu carpeta de spam.'
                  : "Don't see it? Check your spam folder."}
              </p>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-border">
            <Link
              to="/login"
              className="flex items-center justify-center gap-2 text-primary hover:text-primary/80 font-inter transition-colors"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              {language === 'es' ? 'Volver al inicio de sesión' : 'Back to login'}
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
